// src/components/admin/tabs/pointsCenter/PointsSettlementPane.jsx
// 积分中心 · 项目核算：项目选择 + 项目级 KPI（终剪时长/总成本/实际¥每分钟 vs 标准/差额）+ 成员核算表 + 未归属桶。
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Info, RefreshCw } from "lucide-react";
import { fetchPointProjectSettlement } from "../../../../services/adminService";
import { displayUserId, formatMoney } from "../../adminUtils";
import { EmptyBlock, ErrorNotice, Panel, ProjectCombobox, StatCard, StatusBadge } from "../../adminUi.jsx";
import { DateRangeControl } from "./pointsShared.jsx";
import { formatMinutes, formatRate, rangeOfPreset } from "./pointsShared.js";

const formatPerMin = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return "-";
  return `¥${n.toFixed(2)}`;
};

const PointsSettlementPane = ({ projectOptions = [] }) => {
  const [projectId, setProjectId] = useState("");
  const [range, setRange] = useState(() => rangeOfPreset("30d"));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState("");
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    if (!projectId) {
      setData(null);
      return;
    }
    const seq = ++requestSeq.current;
    setLoading(true);
    setError("");
    try {
      const res = await fetchPointProjectSettlement({ projectId, from: range.from, to: range.to });
      if (seq !== requestSeq.current) return;
      setData(res || null);
      setLoadedOnce(true);
    } catch (e) {
      console.error(e);
      if (seq === requestSeq.current) setError(e?.message || "项目核算数据加载失败（核算接口可能未上线）");
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [projectId, range]);

  useEffect(() => { load(); }, [load]);

  const project = data?.project || null;
  const members = useMemo(() => (data?.members || []).slice().sort((a, b) => Number(b.costCny || 0) - Number(a.costCny || 0)), [data]);
  const unattributed = data?.unattributed || null;
  const hasStandard = project && project.targetDurationMinutes != null && project.costStandardCnyPerMin != null;

  const diffColor = (value) => {
    const n = Number(value || 0);
    if (n > 0) return "var(--color-error)"; // 超支
    if (n < 0) return "var(--color-success)"; // 节省
    return "var(--color-text-primary)";
  };

  return (
    <div className="space-y-4">
      {/* 控制条：项目 + 时段 */}
      <div
        className="rounded-lg border px-4 py-3 flex flex-wrap items-end gap-x-4 gap-y-3"
        style={{ backgroundColor: "var(--color-bg-secondary)", borderColor: "var(--color-border-secondary)" }}
      >
        <div>
          <label className="text-label block mb-1.5">项目</label>
          <ProjectCombobox
            value={projectId}
            onChange={setProjectId}
            projectOptions={projectOptions}
            placeholder="选择要核算的项目"
            maxWidth={300}
          />
        </div>
        <div>
          <label className="text-label block mb-1.5">统计时段</label>
          <DateRangeControl from={range.from} to={range.to} onRangeChange={setRange} />
        </div>
        {projectId && (
          <button
            type="button"
            onClick={load}
            title="刷新"
            className="w-8 h-8 rounded-md border flex items-center justify-center cursor-pointer transition-colors duration-150"
            style={{ borderColor: "var(--color-border-primary)", color: "var(--color-text-secondary)", backgroundColor: "var(--color-bg-tertiary)" }}
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
        )}
      </div>

      {error && <ErrorNotice message={error} />}

      {!projectId ? (
        <EmptyBlock text="请选择要核算的项目" hint="按终剪时间轴归因成员产出，核算 ¥/分钟 与奖金差额" />
      ) : !loadedOnce && loading ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{[0, 1, 2, 3].map((i) => <div key={i} className="skeleton h-20" />)}</div>
          <div className="skeleton h-56" />
        </div>
      ) : !project ? (
        <EmptyBlock text="没有核算数据" hint="该项目在该时段内可能没有终剪内容或生成记录" />
      ) : (
        <>
          {/* 项目级 KPI */}
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3">
            <StatCard label="终剪总时长" value={`${formatMinutes(project.totalMinutes)} 分钟`} />
            <StatCard label="总成本" value={formatMoney(project.totalCostCny)} color="var(--color-blue-primary)" />
            <StatCard label="实际 ¥/分钟" value={formatPerMin(project.actualCnyPerMin)} />
            <StatCard
              label="标准 ¥/分钟"
              value={hasStandard ? formatPerMin(project.costStandardCnyPerMin) : "未设置"}
            />
            <StatCard
              label="成本差额"
              value={hasStandard ? formatMoney(project.diffCny) : "-"}
              color={hasStandard ? diffColor(project.diffCny) : undefined}
            />
          </div>

          {!hasStandard && (
            <div
              className="rounded-lg px-3.5 py-2.5 text-sm flex items-center gap-2 animate-in fade-in duration-200"
              style={{ backgroundColor: "color-mix(in srgb, var(--color-warning) 10%, transparent)", border: "1px solid color-mix(in srgb, var(--color-warning) 35%, transparent)", color: "var(--color-warning)" }}
            >
              <Info size={14} className="shrink-0" />
              该项目未设置标准（目标时长 / 成本标准 ¥/分钟），差额与奖金核算不可用；可在项目设置中补充 targetDurationMinutes 与 costStandardCnyPerMin。
            </div>
          )}

          {hasStandard && (
            <div className="text-2xs font-mono-num" style={{ color: "var(--color-text-muted)" }}>
              目标时长 {formatMinutes(project.targetDurationMinutes)} 分钟 · 标准总成本 {formatMoney(project.standardTotalCostCny)} · 差额 = 总成本 − 标准总成本（正=超支，负=节省）
            </div>
          )}

          {/* 成员核算表 */}
          <Panel
            title="成员核算"
            sub="内容归属制：谁生成的内容进入成片，分钟数归谁；总成本含废卡"
            right={<span className="font-mono-num text-2xs" style={{ color: "var(--color-text-muted)" }}>{members.length} 名成员</span>}
          >
            {!members.length ? (
              <EmptyBlock text="该时段内没有成员产出" hint="终剪内容无法归属到生成者时会计入下方「未归属」" />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-label text-left">
                      <th className="font-medium pb-2.5 pr-4">成员</th>
                      <th className="font-medium pb-2.5 pr-4 text-right">产出分钟</th>
                      <th className="font-medium pb-2.5 pr-4 text-right">总成本</th>
                      <th className="font-medium pb-2.5 pr-4 text-right">效率 ¥/分钟</th>
                      <th className="font-medium pb-2.5 pr-4 text-right">抽卡率</th>
                      <th className="font-medium pb-2.5 text-right">奖金差额</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => {
                      const bonus = Number(member.bonusDiffCny || 0);
                      return (
                        <tr
                          key={member.userId}
                          className="hover:bg-[var(--color-bg-tertiary)] transition-colors"
                          style={{ borderTop: "1px solid var(--color-border-secondary)" }}
                        >
                          <td className="py-2.5 pr-4 text-xs font-medium">{displayUserId(member.userId)}</td>
                          <td className="py-2.5 pr-4 text-xs font-mono-num text-right">{formatMinutes(member.minutes)}</td>
                          <td className="py-2.5 pr-4 text-xs font-mono-num text-right">{formatMoney(member.costCny)}</td>
                          <td className="py-2.5 pr-4 text-xs font-mono-num text-right">{formatPerMin(member.efficiencyCnyPerMin)}</td>
                          <td className="py-2.5 pr-4 text-xs font-mono-num text-right">{formatRate(member.gachaRate)}</td>
                          <td
                            className="py-2.5 text-xs font-mono-num font-semibold text-right whitespace-nowrap"
                            style={{ color: bonus > 0 ? "var(--color-success)" : bonus < 0 ? "var(--color-error)" : "var(--color-text-primary)" }}
                            title={hasStandard ? "（标准¥/分钟 − 成员效率）× 产出分钟；正=应补奖金，负=应扣奖金" : "未设置标准"}
                          >
                            {!hasStandard ? "-" : `${bonus > 0 ? "+" : ""}${formatMoney(bonus)}`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {unattributed && (Number(unattributed.minutes || 0) > 0 || Number(unattributed.costCny || 0) > 0) && (
              <div
                className="mt-3 rounded-lg border border-dashed px-3.5 py-2.5 flex flex-wrap items-center gap-x-4 gap-y-1"
                style={{ borderColor: "var(--color-border-primary)" }}
              >
                <StatusBadge label="未归属" color="var(--color-text-muted)" />
                <span className="text-xs" style={{ color: "var(--color-text-secondary)" }}>
                  本地上传 / 外链素材等无法反查生成者的内容
                </span>
                <span className="text-xs font-mono-num ml-auto" style={{ color: "var(--color-text-muted)" }}>
                  {formatMinutes(unattributed.minutes)} 分钟 · {formatMoney(unattributed.costCny)}
                </span>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
};

export default PointsSettlementPane;
